/**
 * @al-life/types
 * Shared TypeScript types used by both frontend (apps/web) and backend (apps/api).
 * Keep this lean — only types that truly cross the boundary belong here.
 */

// ─── API Response Envelope ────────────────────────────────────────────────────

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: string;
}

// ─── Health Check ─────────────────────────────────────────────────────────────

export type ServiceStatus = "connected" | "disconnected" | "unknown";

export interface HealthCheckData {
  status: "ok" | "degraded" | "error";
  stage: string;
  version: string;
  services: {
    database: ServiceStatus;
    cloudinary: ServiceStatus;
  };
  uptime: number;
  timestamp: string;
}

export type HealthCheckResponse = ApiResponse<HealthCheckData>;

// ─── Usage / cost (admin-only) ──────────────────────────────────────────────────

/**
 * Returned by `GET /admin/usage` (admin only). Each block is independently
 * optional: a metric source that's unavailable (e.g. Cost Explorer not enabled,
 * a transient API error) comes back as `available: false` with a reason, so the
 * dashboard degrades gracefully instead of failing wholesale.
 */
export interface UsageMetric {
  available: boolean;
  reason?: string; // why it's unavailable, when available === false
}

/** A used-vs-limit gauge (free-tier headroom). */
export interface UsageGauge extends UsageMetric {
  used: number;
  limit: number;
  /** 0–100, precomputed for the bar. */
  percent: number;
  unit: string; // e.g. "requests", "credits"
}

export interface UsageData {
  /** UTC month window the figures cover, e.g. "2026-06". */
  month: string;
  /** Lambda invocations this month vs the 1M/month perpetual free tier. */
  lambdaInvocations: UsageGauge;
  /** API Gateway (HTTP API) requests this month. No perpetual free tier. */
  apiGatewayRequests: UsageGauge;
  /** Cloudinary plan usage — credits are shared across storage/bandwidth/transforms. */
  cloudinary: UsageGauge & {
    /** Storage in GB, if the plan reports it separately. */
    storageGb?: number;
    planName?: string;
  };
  /** AWS month-to-date cost (Cost Explorer). */
  cost: UsageMetric & {
    currency?: string;
    monthToDate?: number;
    /** Per-service breakdown, largest first. */
    byService?: { service: string; amount: number }[];
  };
}

export type UsageResponse = ApiResponse<UsageData>;

// ─── Image Upload (Cloudinary) ──────────────────────────────────────────────────

/**
 * Returned by `POST /uploads/sign`. The browser uses these fields to upload
 * a file directly to Cloudinary (the API secret never leaves the backend).
 */
export interface CloudinarySignature {
  timestamp: number;
  folder: string;
  signature: string;
  apiKey: string;
  cloudName: string;
}

// ─── User / Auth ──────────────────────────────────────────────────────────────

/** Authorisation tier. `admin` is set MANUALLY in MongoDB — there is no admin UI. */
export type UserRole = "user" | "admin";

/**
 * The bundled cartoon avatars. These are static SVGs shipped with the web app
 * (apps/web/src/assets/avatars) — we only ever store the *key* on the user, never
 * an uploaded image, so avatars cost zero storage.
 */
export const AVATAR_KEYS = [
  "fox",
  "cat",
  "panda",
  "rabbit",
  "bear",
  "penguin",
  "owl",
  "koala",
  "frog",
  "lion",
  "dog",
  "hamster",
] as const;

export type AvatarKey = (typeof AVATAR_KEYS)[number];

export const DEFAULT_AVATAR: AvatarKey = "fox";

export function isAvatarKey(value: unknown): value is AvatarKey {
  return typeof value === "string" && (AVATAR_KEYS as readonly string[]).includes(value);
}

/** The public shape of a user — safe to send to the browser (no password hash). */
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  avatarKey: AvatarKey;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Returned by login / verify-email — a session token plus the user it belongs to. */
export interface AuthSession {
  token: string;
  user: AuthUser;
}

// ─── Auth request bodies ────────────────────────────────────────────────────────

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  avatarKey: AvatarKey;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface ResendVerificationRequest {
  email: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface UpdateProfileRequest {
  name?: string;
  avatarKey?: AvatarKey;
}

// ─── Account requests ("request access") ──────────────────────────────────────
//
// Self-signup is disabled while AWS SES is in the sandbox (verification email
// can't reach real users). Instead a visitor REQUESTS an account: they pick their
// name/email/password/avatar (password hashed at rest, never returned) and the
// admin approves it in the message center, which flips the request into a real,
// already-verified user. A pending/dismissed request is never a login.

export type AccountRequestStatus = "pending" | "approved" | "dismissed";

/** Public shape of an account request — NEVER includes the password hash. */
export interface AccountRequest {
  id: string;
  name: string;
  email: string;
  avatarKey: AvatarKey;
  message?: string; // optional note ("why I'd like access")
  status: AccountRequestStatus;
  createdAt: string;
  handledAt?: string;
}

/** Body for POST /auth/requests — same fields as register plus an optional note. */
export interface CreateAccountRequestRequest {
  name: string;
  email: string;
  password: string;
  avatarKey: AvatarKey;
  message?: string;
}

// ─── Notifications (admin message center) ─────────────────────────────────────

/**
 * One call feeds both the unread badge and the message-center panel. `unread` =
 * pending account requests + unhandled cooking requests (shared flags — there's
 * effectively one admin, so no per-user read receipts needed).
 */
export interface NotificationSummary {
  accountRequests: AccountRequest[];
  cookingRequests: DishRequest[];
  unread: number;
}

// ─── Spaces (multi-tenant) ────────────────────────────────────────────────────
//
// Every piece of content belongs to a Space — a per-person (or per-couple) area
// addressed by a URL slug (`/anmemory/travel`). A Space usually has one member,
// but Allen + Norah co-own one. Content is private by default; a member can mark
// individual sections public so anyone with the link can view them (read-only).

/** The content areas a Space contains — also the keys used in `publicSections`. */
export const SPACE_SECTIONS = ["pets", "reading", "travel", "cooking", "diary", "guides"] as const;
export type SpaceSection = (typeof SPACE_SECTIONS)[number];

export function isSpaceSection(value: unknown): value is SpaceSection {
  return typeof value === "string" && (SPACE_SECTIONS as readonly string[]).includes(value);
}

/**
 * Sections that can NEVER be shared publicly — always members-only, no matter
 * what `publicSections` says. The Love Diary is personal to the couple: even a
 * space browsed as a read-only example must not expose its diary; non-members
 * only ever see an explainer of what the section is.
 */
export const PRIVATE_SECTIONS: readonly SpaceSection[] = ["diary"];

export function isPrivateSection(section: SpaceSection): boolean {
  return PRIVATE_SECTIONS.includes(section);
}

/**
 * Public shape of a Space. `canEdit` is computed per-caller (member = true);
 * `isMember` tells the client whether the viewer belongs here. Never exposes
 * raw member ObjectIds beyond what the UI needs.
 */
export interface Space {
  id: string;
  slug: string;
  name: string;
  /** Sections open to the public (viewable without login). */
  publicSections: SpaceSection[];
  /** True when the current caller may edit this space (is a member). */
  canEdit: boolean;
  /** True when the current caller is a member (same as canEdit today). */
  isMember: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateSpaceRequest {
  name?: string;
  publicSections?: SpaceSection[];
}

// ─── Travel Map ─────────────────────────────────────────────────────────────
//
// City-level memory map. Each visited city is a pin; a country colours when ≥1
// of its cities is visited (shade by visited-city count). Place identity (name,
// country, continent, coordinates) is snapshotted from the bundled GeoNames
// dataset on add, so the map and stats never re-query the dataset. Bilingual
// guides reuse `Bilingual { en, cn }` (defined below in the Reading section).

/** One dated visit to a city. A city stores ≥1 of these (repeat visits welcome). */
export interface TravelVisit {
  date: string; // YYYY-MM-DD (start)
  endDate?: string; // YYYY-MM-DD (optional; for multi-day stays)
  note?: string; // short one-liner for this specific visit (optional)
}

/**
 * A visited city — one pin on the map, one row in `travelCities`.
 *
 * Dates are plain `YYYY-MM-DD` strings (timezone-safe). Country colour is
 * DERIVED (the map counts visited cities per `countryCode`), never stored.
 */
export interface TravelCity {
  id: string;
  // ── canonical place identity (snapshotted from the dataset on add) ──
  geonameId: number;
  name: Bilingual; // city name, e.g. { en: "Tokyo", cn: "东京" } (cn may be empty)
  countryCode: string; // ISO2, e.g. "JP" → drives country colouring
  countryName: Bilingual; // denormalised for display/stats (cn may be empty)
  continent: string; // continent code, e.g. "AS" (resolved to a label via lang)
  lat: number;
  lng: number;
  // ── our memory of the place ──
  visits: TravelVisit[]; // ≥1 dated visit (for a home, the span = time lived there)
  rating: number; // 1–5★
  guide: Bilingual; // Markdown body, the city 攻略 (either side may be empty)
  // NOTE: "home" status is DERIVED, not stored — a city is a home if any visit
  // (stay) is ≥183 days. See features/travel/homeMeta.ts. No isHome flag.
  createdAt: string;
  updatedAt: string;
}

export interface CreateTravelCityRequest {
  geonameId: number;
  name: Bilingual;
  countryCode: string;
  countryName: Bilingual;
  continent: string;
  lat: number;
  lng: number;
  visits: TravelVisit[];
  rating: number;
  guide: Bilingual;
}

export type UpdateTravelCityRequest = Partial<CreateTravelCityRequest>;

/**
 * A country-level travel guide (攻略). A country can hold several — e.g.
 * "Hokkaido — 5 days", "Tokyo area — 4 days". Sorted by `order` within a country.
 */
export interface TravelCountryGuide {
  id: string;
  countryCode: string; // ISO2 it belongs to
  title: Bilingual; // e.g. { en: "Hokkaido — 5 days", cn: "北海道 5 天" }
  areaLabel?: Bilingual; // optional sub-region tag
  days?: number; // optional duration
  body: Bilingual; // Markdown
  order: number; // manual sort within the country
  createdAt: string;
  updatedAt: string;
}

export interface CreateTravelCountryGuideRequest {
  countryCode: string;
  title: Bilingual;
  areaLabel?: Bilingual;
  days?: number;
  body: Bilingual;
  order?: number;
}

export type UpdateTravelCountryGuideRequest = Partial<CreateTravelCountryGuideRequest>;

// ─── Travel Routes (出行足迹) ─────────────────────────────────────────────────
//
// Flight / train / car route history — 航旅纵横/Flighty style. One doc per
// one-way trip between two places (a round trip = two docs), drawn as a
// great-circle arc on the Routes tab. Endpoints are snapshotted from the
// bundled GeoNames dataset (same flow as travel cities), so rendering and
// future stats never re-query the dataset. Distance is DERIVED (haversine),
// never stored.

export const TRAVEL_ROUTE_MODES = ["flight", "train", "car"] as const;
export type TravelRouteMode = (typeof TRAVEL_ROUTE_MODES)[number];

export function isTravelRouteMode(v: unknown): v is TravelRouteMode {
  return typeof v === "string" && (TRAVEL_ROUTE_MODES as readonly string[]).includes(v);
}

/** One endpoint of a route — a place snapshotted from the dataset on add. */
export interface TravelRoutePlace {
  name: Bilingual; // e.g. { en: "Sydney", cn: "悉尼" } (cn may be empty)
  lat: number;
  lng: number;
  countryCode?: string; // ISO2 (kept for future stats/flags)
}

/** One one-way trip — a single arc on the route map. */
export interface TravelRoute {
  id: string;
  mode: TravelRouteMode;
  from: TravelRoutePlace;
  to: TravelRoutePlace;
  date: string; // YYYY-MM-DD
  label?: string; // optional flight/train number, e.g. "CA178" / "G7124"
  /** Optional aircraft model, e.g. "A330-300" / "B737-800". The AIRLINE is
   * derived from the flight-number prefix in `label` — never stored. */
  aircraft?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTravelRouteRequest {
  mode: TravelRouteMode;
  from: TravelRoutePlace;
  to: TravelRoutePlace;
  date: string;
  label?: string;
  aircraft?: string;
}

export type UpdateTravelRouteRequest = Partial<CreateTravelRouteRequest>;

// ─── Cooking Menu (菜单) ──────────────────────────────────────────────────────
//
// A menu of every dish we make. The menu (photo + name + badges + rating +
// ingredients) is PUBLIC; only the cooking **steps** are admin-only and are
// stripped server-side for non-admins. Dishes are classified on three
// independent axes — Course (one), Cuisine (one), Tags (many) — so the
// collection stays richly filterable. `Bilingual { en, cn }` (defined in the
// Reading section below) carries all bilingual text. Logged-in users can send
// the admin a "request" (a cart of dishes to please cook) — a lightweight order.

/** Axis 1 — Course / 类型 (required, single-select). What role the dish plays.
 * A focused 6-course set (前菜 · 主菜 · 主食 · 汤 · 点心 · 特色菜). The stored value
 * is always the English key; Chinese labels live in the web `lang.ts`. */
export const DISH_COURSES = [
  "Appetizer", // 前菜
  "Main", // 主菜
  "Staple", // 主食
  "Soup", // 汤
  "Snack", // 点心 (snack / dim sum / dessert)
  "Specialty", // 特色菜
] as const;

export type DishCourse = (typeof DISH_COURSES)[number];

export function isDishCourse(v: unknown): v is DishCourse {
  return typeof v === "string" && (DISH_COURSES as readonly string[]).includes(v);
}

/**
 * Maps the *old* 11-course keys onto the new 6-course set. Used by the one-off
 * migration (`scripts/migrate-dish-courses.mjs`) AND kept here as a safety net
 * so any stray legacy value still resolves to a valid course in the UI. New
 * dishes never use the old keys.
 */
export const LEGACY_COURSE_MAP: Record<string, DishCourse> = {
  "Cold dish": "Appetizer",
  Salad: "Appetizer",
  "Hot dish": "Main",
  Soup: "Soup",
  Staple: "Staple",
  Breakfast: "Staple",
  Snack: "Snack",
  Dessert: "Snack",
  Baking: "Snack",
  Drinks: "Snack",
  Sauce: "Specialty",
};

/** Normalise any (possibly legacy) course value to a valid current course. */
export function normalizeCourse(v: unknown): DishCourse {
  if (isDishCourse(v)) return v;
  if (typeof v === "string" && LEGACY_COURSE_MAP[v]) return LEGACY_COURSE_MAP[v];
  return "Main";
}

/**
 * Axis 2 — Cuisine / 菜系 (required, single-select, grouped in the UI). Chinese
 * regional cuisines are first-class values. `group` drives the optgroup in the
 * dropdown and the grouping in stats; it is metadata, not stored on a dish.
 */
export const DISH_CUISINE_GROUPS = ["chinese", "foreign", "creative"] as const;
export type DishCuisineGroup = (typeof DISH_CUISINE_GROUPS)[number];

export const DISH_CUISINES = [
  // 中餐 Chinese
  "Home-style",
  "Sichuan",
  "Cantonese",
  "Chaoshan",
  "Hakka",
  "Hunan",
  "Shandong",
  "Jiangsu",
  "Shanghainese",
  "Zhejiang",
  "Fujian",
  "Anhui",
  "Beijing",
  "Shanxi",
  "Shaanxi",
  "Northeastern",
  "Northwestern",
  "Xinjiang",
  "Yunnan",
  "Guizhou",
  "Taiwanese",
  "Hong Kong",
  "Hotpot",
  "BBQ",
  // 西餐 Western
  "Italian",
  "French",
  "Spanish",
  "American",
  "Mediterranean",
  "British",
  "German",
  "Australian",
  // 亚洲其他 Other Asian
  "Japanese",
  "Korean",
  "Thai",
  "Vietnamese",
  "Malaysian",
  "Indonesian",
  "Indian",
  // 其他 Other
  "Middle-Eastern",
  "Mexican",
  "Fusion",
  "Baking",
  "Other",
] as const;

export type DishCuisine = (typeof DISH_CUISINES)[number];

export function isDishCuisine(v: unknown): v is DishCuisine {
  return typeof v === "string" && (DISH_CUISINES as readonly string[]).includes(v);
}

/** Which group a cuisine belongs to (for the two-level dropdown + stats).
 * 中餐 = all Chinese regional + Hotpot/BBQ; 外国料理 = Western + other Asian +
 * Mexican/Middle-Eastern; 创意菜 = Fusion + Other. */
export const DISH_CUISINE_GROUP_OF: Record<DishCuisine, DishCuisineGroup> = {
  "Home-style": "chinese",
  Sichuan: "chinese",
  Cantonese: "chinese",
  Chaoshan: "chinese",
  Hakka: "chinese",
  Hunan: "chinese",
  Shandong: "chinese",
  Jiangsu: "chinese",
  Shanghainese: "chinese",
  Zhejiang: "chinese",
  Fujian: "chinese",
  Anhui: "chinese",
  Beijing: "chinese",
  Shanxi: "chinese",
  Shaanxi: "chinese",
  Northeastern: "chinese",
  Northwestern: "chinese",
  Xinjiang: "chinese",
  Yunnan: "chinese",
  Guizhou: "chinese",
  Taiwanese: "chinese",
  "Hong Kong": "chinese",
  Hotpot: "chinese",
  BBQ: "chinese",
  Italian: "foreign",
  French: "foreign",
  Spanish: "foreign",
  American: "foreign",
  Mediterranean: "foreign",
  British: "foreign",
  German: "foreign",
  Australian: "foreign",
  Japanese: "foreign",
  Korean: "foreign",
  Thai: "foreign",
  Vietnamese: "foreign",
  Malaysian: "foreign",
  Indonesian: "foreign",
  Indian: "foreign",
  "Middle-Eastern": "foreign",
  Mexican: "foreign",
  Fusion: "creative",
  Baking: "creative",
  Other: "creative",
};

/** Axis 3 — Tags / 标签 (multi-select — flavour/diet, method, occasion/effort).
 * Kept in ALPHABETICAL order — this drives the display order of the tag picker,
 * the filter chips, and the by-tag view. The method subset lives in
 * `DISH_METHOD_TAGS` below. */
export const DISH_TAGS = [
  "Air-fryer",
  "Boiled",
  "Braised",
  "Breakfast",
  "Cold-mixed",
  "Festive",
  "For guests",
  "Fried",
  "Gluten-free",
  "Healthy",
  "High-protein",
  "Late-night",
  "Light",
  "Low-cal",
  "Meal-prep",
  "One-pot",
  "Pan-fried",
  "Quick",
  "Raw",
  "Rice-killer",
  "Rich",
  "Roast",
  "Seafood",
  "Spicy",
  "Steamed",
  "Stir-fry",
  "Sweet",
  "Vegan",
  "Vegetarian",
] as const;

export type DishTag = (typeof DISH_TAGS)[number];

/** The subset of tags that are cooking *methods* — used by the stats split. */
export const DISH_METHOD_TAGS: readonly DishTag[] = [
  "Stir-fry",
  "Fried",
  "Pan-fried",
  "Roast",
  "Steamed",
  "Braised",
  "Boiled",
  "Cold-mixed",
  "Raw",
  "Air-fryer",
];

/** Max 辣度 (spiciness) level — rendered as up to this many chili icons. */
export const DISH_SPICINESS_MAX = 5;

export interface DishIngredient {
  name: Bilingual; // either side may be empty
  amount?: string; // e.g. "200g", "2 tbsp"
}

/** The private know-how. Only ever sent to admins (stripped server-side). */
export interface DishRecipe {
  steps: Bilingual; // Markdown (bilingual)
}

/**
 * A dish on the menu — one row in `dishes`.
 *
 * Everything here EXCEPT `recipe` is public. `recipe` (the cooking steps) is
 * omitted from the payload for non-admins by the list/get handlers, so the
 * know-how never leaves the server unless you're an admin. Ingredients are
 * public (shown to entice).
 */
export interface Dish {
  id: string;
  name: Bilingual;
  photoUrl?: string;
  photoPublicId?: string; // for Cloudinary cleanup on replace/delete
  course: DishCourse; // Axis 1
  cuisine: DishCuisine; // Axis 2
  tags: DishTag[]; // Axis 3
  ingredients: DishIngredient[]; // public
  rating?: number; // 1–5★
  spiciness?: number; // 辣度 0–5 (0/undefined = not spicy); shown as chili icons
  favorite?: boolean;
  /** 招牌菜 — a house signature dish; shown as a gold "招牌 / Signature" badge. */
  signature?: boolean;
  /** False temporarily pauses ordering; absent legacy rows remain orderable. */
  available?: boolean;
  prepTimeMinutes?: number;
  cookTimeMinutes?: number;
  servings?: number;
  createdAt: string;
  updatedAt: string;
  /** Admin-only cooking steps — present in the payload only for admins. */
  recipe?: DishRecipe;
}

export interface CreateDishRequest {
  name: Bilingual;
  photoUrl?: string;
  photoPublicId?: string;
  course: DishCourse;
  cuisine: DishCuisine;
  tags: DishTag[];
  ingredients: DishIngredient[];
  rating?: number;
  spiciness?: number; // 辣度 0–5
  favorite?: boolean;
  signature?: boolean; // 招牌菜
  /** Defaults to true. Set false to keep the dish visible but pause ordering. */
  available?: boolean;
  prepTimeMinutes?: number;
  cookTimeMinutes?: number;
  servings?: number;
  recipe: DishRecipe;
}

export type UpdateDishRequest = Partial<CreateDishRequest>;

// ─── Cooking: Drinks (酒水单) ──────────────────────────────────────────────────

/**
 * Drink categories, in DISPLAY PRIORITY ORDER — the drinks tab groups by this
 * array, so cocktails lead and "other" trails. Closed set, like `DISH_COURSES`:
 * one source of truth for the form dropdown, the filter chips and the stats.
 */
export const DRINK_CATEGORIES = [
  "鸡尾酒 Cocktail",
  "无酒精 Soft drink",
  "葡萄酒 Wine",
  "啤酒 Beer",
  "烈酒 Spirits",
  "茶与咖啡 Tea & Coffee",
  "其他 Other",
] as const;

export type DrinkCategory = (typeof DRINK_CATEGORIES)[number];

export function isDrinkCategory(v: unknown): v is DrinkCategory {
  return typeof v === "string" && (DRINK_CATEGORIES as readonly string[]).includes(v);
}

/**
 * Categories that can carry a base spirit / ABV / glassware. A soft drink or a
 * tea never asks for its base spirit — the form hides those fields entirely.
 */
export const ALCOHOLIC_CATEGORIES: readonly DrinkCategory[] = [
  "鸡尾酒 Cocktail",
  "葡萄酒 Wine",
  "啤酒 Beer",
  "烈酒 Spirits",
];

export function isAlcoholicCategory(c: DrinkCategory): boolean {
  return ALCOHOLIC_CATEGORIES.includes(c);
}

/** Base spirit for a cocktail (or a neat pour). Alphabetical, "None" last. */
export const BASE_SPIRITS = [
  "Brandy",
  "Gin",
  "Liqueur",
  "Rum",
  "Tequila",
  "Vodka",
  "Whisky",
  "None",
] as const;

export type BaseSpirit = (typeof BASE_SPIRITS)[number];

export function isBaseSpirit(v: unknown): v is BaseSpirit {
  return typeof v === "string" && (BASE_SPIRITS as readonly string[]).includes(v);
}

/** What it's served in. Kept short — enough to picture the drink. */
export const GLASSWARE = [
  "Coupe",
  "Rocks",
  "Highball",
  "Martini",
  "Wine glass",
  "Flute",
  "Mug",
  "Shot",
  "Other",
] as const;

export type Glassware = (typeof GLASSWARE)[number];

export function isGlassware(v: unknown): v is Glassware {
  return typeof v === "string" && (GLASSWARE as readonly string[]).includes(v);
}

/** Flavour/serve tags for drinks. Alphabetical, like `DISH_TAGS`. */
export const DRINK_TAGS = [
  "Bitter",
  "Citrus",
  "Classic",
  "Creamy",
  "Fizzy",
  "Frozen",
  "Fruity",
  "Herbal",
  "Hot",
  "Iced",
  "Low-ABV",
  "Party",
  "Refreshing",
  "Signature serve",
  "Smoky",
  "Sour",
  "Spicy",
  "Strong",
  "Sweet",
] as const;

export type DrinkTag = (typeof DRINK_TAGS)[number];

/** Highest ABV we accept — a sanity bound, not a real-world limit. */
export const DRINK_ABV_MAX = 70;

/** Same shape as `DishIngredient` — public, shown to entice. */
export interface DrinkIngredient {
  name: Bilingual;
  amount?: string; // e.g. "45ml", "2 dashes"
}

/** The private know-how. Only ever sent to admins (stripped server-side). */
export interface DrinkRecipe {
  steps: Bilingual; // Markdown (bilingual)
}

/**
 * A drink on the 酒水单 — one row in `drinks`.
 *
 * Deliberately its own type rather than a `Dish` with a drink course: a
 * cocktail's base spirit, ABV and glassware have no analogue on a dish, and
 * course/cuisine/spiciness have none here. Privacy mirrors `Dish` exactly —
 * everything except `recipe` is public.
 */
export interface Drink {
  id: string;
  name: Bilingual;
  photoUrl?: string;
  photoPublicId?: string; // for Cloudinary cleanup on replace/delete
  category: DrinkCategory;
  tags: DrinkTag[];
  ingredients: DrinkIngredient[]; // public
  rating?: number; // 1–5★
  favorite?: boolean;
  /** 招牌 — a house signature serve; gold badge, as on dishes. */
  signature?: boolean;
  /** False temporarily pauses ordering; absent legacy rows remain orderable. */
  available?: boolean;
  /** Alcohol by volume %, 0–DRINK_ABV_MAX. Only meaningful for alcoholic categories. */
  abv?: number;
  baseSpirit?: BaseSpirit;
  glassware?: Glassware;
  prepTimeMinutes?: number;
  servings?: number;
  createdAt: string;
  updatedAt: string;
  /** Admin-only mixing steps — present in the payload only for admins. */
  recipe?: DrinkRecipe;
}

export interface CreateDrinkRequest {
  name: Bilingual;
  photoUrl?: string;
  photoPublicId?: string;
  category: DrinkCategory;
  tags: DrinkTag[];
  ingredients: DrinkIngredient[];
  rating?: number;
  favorite?: boolean;
  signature?: boolean;
  /** Defaults to true. Set false to keep the drink visible but pause ordering. */
  available?: boolean;
  abv?: number;
  baseSpirit?: BaseSpirit;
  glassware?: Glassware;
  prepTimeMinutes?: number;
  servings?: number;
  recipe: DrinkRecipe;
}

export type UpdateDrinkRequest = Partial<CreateDrinkRequest>;

// ─── Cooking: Requests ("orders") ───────────────────────────────────────────────

/** What a cart item points at. Absent on rows written before drinks existed. */
export type RequestItemKind = "dish" | "drink";

/**
 * One item in a request's cart — a dish OR a drink. The name is snapshotted so
 * the inbox still reads fine even if the item is later edited or deleted.
 *
 * NOTE `dishId` is the id of a dish *or* a drink, per `kind`. The field keeps
 * its original name because renaming it would break every stored row for pure
 * tidiness; `kind` is what disambiguates. `kind` is optional and read as
 * `"dish"` when absent, so orders placed before drinks existed keep working
 * without a migration.
 */
export interface DishRequestItem {
  dishId: string;
  dishName: Bilingual;
  kind?: RequestItemKind;
}

/** A cart item's kind, defaulting legacy rows to "dish". */
export function itemKind(item: Pick<DishRequestItem, "kind">): RequestItemKind {
  return item.kind === "drink" ? "drink" : "dish";
}

/** A request to please cook a cart of dishes. Public-menu visitors can order too. */
export interface DishRequest {
  id: string;
  /** Present when a signed-in visitor placed the order. */
  requesterId?: string;
  requesterName: string; // snapshot (display name)
  /** ISO 8601 collection / arrival time; absent only on pre-arrival legacy orders. */
  arrivalAt?: string;
  message?: string; // optional note
  items: DishRequestItem[]; // one or more dishes
  /** Admin has actioned it — drives the "unread" count (unhandled = unread). */
  handled?: boolean;
  /**
   * Which space this order belongs to. Populated only by the cross-space admin
   * message center (`/notifications/summary`) so it can act on the right space;
   * the space-scoped `/spaces/{slug}/cooking/requests` endpoints omit it.
   */
  spaceSlug?: string;
  createdAt: string;
}

export interface CreateDishRequestRequest {
  requesterName: string;
  arrivalAt: string;
  message?: string;
  items: DishRequestItem[];
}

// ─── Pets ─────────────────────────────────────────────────────────────────────

export interface Pet {
  id: string;
  name: string;
  species: string;
  breed?: string;
  birthDate?: string;
  avatarUrl?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Pet Care: Bookings (shared calendar) ──────────────────────────────────────

/** The kinds of care a booking can be for. Kept as a closed set for filtering/icons. */
export const CARE_TYPES = [
  "Walk",
  "Feed",
  "Grooming",
  "Vet",
  "Daycare",
  "House Host",
  "Medication",
  "Other",
] as const;

export type CareType = (typeof CARE_TYPES)[number];

export function isCareType(value: unknown): value is CareType {
  return typeof value === "string" && (CARE_TYPES as readonly string[]).includes(value);
}

/**
 * A pet's sex. Optional everywhere — there is no pet registry, so it's recorded
 * on the booking or history photo where it's known and DERIVED per dog by the
 * analysis (most-recent entry wins). Absent = unknown = no icon.
 */
export const PET_SEXES = ["male", "female"] as const;

export type PetSex = (typeof PET_SEXES)[number];

export function isPetSex(value: unknown): value is PetSex {
  return typeof value === "string" && (PET_SEXES as readonly string[]).includes(value);
}

/**
 * A pet-care booking on the shared calendar.
 *
 * Dates are plain `YYYY-MM-DD` strings (not Date objects) so they're
 * timezone-safe — the 5th is always the 5th regardless of where the viewer is.
 * A booking spans `[date, endDate]` inclusive; a single-day booking has
 * `endDate === date`. The daily start/end *times* (`HH:mm`) apply to each day in
 * the span. Lean by design: one row, no joins.
 */
export interface PetBooking {
  id: string;
  date: string; // YYYY-MM-DD — first day of the span
  endDate: string; // YYYY-MM-DD — last day (inclusive); equals `date` for single-day
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  petName: string;
  petBreed: string;
  /** The pet's sex, when recorded. Meaningless on a multi-dog "Bobby, Max" row. */
  petSex?: PetSex;
  careType: CareType;
  assignedTo: string;
  notes?: string;
  /**
   * What we charge per DAY of the span (AUD). Optional; absent/0 means unpriced.
   * The booking's daily earnings are `dailyRate × spanDays`, so extending the
   * end date automatically re-prices the stay — which is the intent.
   */
  dailyRate?: number;
  /**
   * A flat one-off amount for the WHOLE booking (AUD) — transport, a bath, a
   * holiday surcharge. Added once regardless of how many days the span covers.
   * **May be NEGATIVE**: `dailyRate × days` often overshoots the agreed price,
   * and a signed adjustment here (`-20`) is how the total is made right without
   * faking the daily rate.
   */
  extraCharge?: number;
  /** Display name of the member who created it (snapshot — cheap, avoids a user lookup). */
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePetBookingRequest {
  date: string; // YYYY-MM-DD — first day of the span
  endDate: string; // YYYY-MM-DD — last day (inclusive); equals `date` for single-day
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  petName: string;
  petBreed: string;
  petSex?: PetSex;
  careType: CareType;
  assignedTo: string;
  notes?: string;
  dailyRate?: number;
  /** Signed — see `PetBooking.extraCharge`. */
  extraCharge?: number;
}

/**
 * Magnitude bound on a fee field — catches a fat-fingered entry, not a real
 * price. `dailyRate` is `[0, MAX_FEE]`; `extraCharge` is `[-MAX_FEE, MAX_FEE]`.
 */
export const MAX_FEE = 100_000;

/**
 * Inclusive day-span length between two `YYYY-MM-DD` keys (≥ 1, capped sane).
 * Shared so booking totals, care-days and earnings all count days identically.
 */
export function spanDays(date: string, endDate: string): number {
  const start = Date.parse(`${date}T00:00:00Z`);
  const end = Date.parse(`${endDate || date}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 1;
  const days = Math.round((end - start) / 86_400_000) + 1;
  // Guard against a bad row blowing up a total.
  return Math.min(Math.max(days, 1), 366);
}

/** Round to cents — keeps float drift out of displayed money. */
export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * What a booking earned: `dailyRate × days + extraCharge`. Always derived, never
 * stored. Can be ≤ 0 when a negative extra cancels out the rate.
 */
export function bookingTotal(booking: {
  date: string;
  endDate: string;
  dailyRate?: number;
  extraCharge?: number;
}): number {
  const days = spanDays(booking.date, booking.endDate);
  return roundMoney((booking.dailyRate ?? 0) * days + (booking.extraCharge ?? 0));
}

/**
 * True when a booking carries no price at all — drives the "unpriced" callout.
 * "Priced" means *any* fee was entered, including a negative extra: a `-20`
 * adjustment is deliberate money data, not a missing fee, so a booking whose
 * total nets to $0 still counts (and is never listed as needing a price).
 */
export function isUnpriced(booking: { dailyRate?: number; extraCharge?: number }): boolean {
  return !booking.dailyRate && !booking.extraCharge;
}

export type UpdatePetBookingRequest = Partial<CreatePetBookingRequest>;

// ─── Pet Care: Blocked dates ────────────────────────────────────────────────────

/**
 * A blocked date range on the shared calendar — days marked unavailable so no
 * new booking can be started on them (existing bookings are unaffected). Lean by
 * design: just a `[date, endDate]` span, no pet/care info. Same `YYYY-MM-DD`
 * string + overlap-query model as `PetBooking`.
 */
export interface PetBlockedDate {
  id: string;
  date: string; // YYYY-MM-DD — first day (inclusive)
  endDate: string; // YYYY-MM-DD — last day (inclusive); equals `date` for one day
  reason?: string; // optional short note (e.g. "away")
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePetBlockedDateRequest {
  date: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD (inclusive); defaults to `date`
  reason?: string;
}

// ─── Pet Care: History (photo wall) ─────────────────────────────────────────────

/** A past pet-care moment shown on the history wall. Newest first. */
export interface PetHistoryEntry {
  id: string;
  photoUrl: string;
  dogName: string;
  breed: string;
  /** The dog's sex, when recorded. */
  sex?: PetSex;
  /** When the moment happened (ISO). Drives the newest-first ordering and the displayed date. */
  takenAt: string;
  createdAt: string;
}

export interface CreatePetHistoryRequest {
  photoUrl: string;
  /**
   * The Cloudinary public_id for `photoUrl`. Stored so we can DELETE the image
   * from Cloudinary when the entry is removed or its photo replaced — otherwise
   * orphaned images pile up and burn the free-tier quota.
   */
  photoPublicId?: string;
  dogName: string;
  breed: string;
  sex?: PetSex;
  takenAt: string; // ISO
}

/**
 * Edit an existing moment. Photo fields are optional — omit them to keep the
 * current photo, or send a new `photoUrl` + `photoPublicId` to replace it (the
 * backend deletes the old image from Cloudinary).
 */
export interface UpdatePetHistoryRequest {
  dogName?: string;
  breed?: string;
  /** `null` clears a previously recorded sex; omit to leave it unchanged. */
  sex?: PetSex | null;
  takenAt?: string; // ISO
  photoUrl?: string;
  photoPublicId?: string;
}

// ─── Pet Care: Analysis (insights dashboard) ─────────────────────────────────

/**
 * Pre-computed pet-care analysis, aggregated server-side from ALL bookings (no
 * month limit) plus the latest history photo per dog. Lean payload: the heavy
 * aggregation runs in Mongo/Lambda so the client just renders charts — it never
 * downloads the whole booking/history history. Public read (everyone sees it).
 */
export interface PetAnalysis {
  /** Total bookings counted (a multi-dog "Bobby, Max" booking counts once here). */
  totalBookings: number;
  /** Distinct dogs seen across all bookings (after splitting comma-separated names). */
  totalDogs: number;
  /** Total care-days = sum of each booking's span length (endDate − date + 1). */
  totalCareDays: number;
  /** Distinct months that have at least one booking. */
  activeMonths: number;
  /** Care-type distribution, sorted by count desc. */
  byCareType: { key: CareType; count: number }[];
  /** Who the care was assigned to, sorted desc. */
  byAssignee: { name: string; count: number }[];
  /**
   * Dog-breed (type) distribution — number of distinct DOGS of each breed,
   * sorted desc. Dogs whose breed is unknown are grouped under "Unknown".
   */
  byBreed: { breed: string; dogs: number }[];
  /** Bookings per month for the last 12 months (oldest → newest). */
  perMonth: { month: string; count: number }[];
  /**
   * Care-days per month for the last 12 months (oldest → newest). A stay that
   * crosses a month boundary contributes its days to each month it spans
   * (same day-walk attribution as earnings), so the bars sum to the in-window
   * part of `totalCareDays`.
   */
  careDaysPerMonth: { month: string; days: number }[];
  /**
   * Per-dog leaderboard, sorted by booking count desc. `photoUrl` is the dog's
   * most-recent history photo when one exists (matched by name), else null.
   */
  dogs: PetDogStat[];
}

/** One dog's aggregated stats for the analysis leaderboard. */
export interface PetDogStat {
  /** Display name (the original casing from the most recent booking/history). */
  name: string;
  /** Number of bookings that included this dog. */
  bookings: number;
  /** Sum of span lengths across this dog's bookings (care-days). */
  careDays: number;
  /** Most-recent history photo for this dog, or null if none. */
  photoUrl: string | null;
  /** This dog's breed, from history or a booking — null if unknown. */
  breed: string | null;
  /**
   * This dog's sex, from the most-recent booking or history entry that recorded
   * one (so a correction on the latest entry wins) — null if never recorded.
   */
  sex: PetSex | null;
  /** The dog's top care type (most frequent), for a quick descriptor. */
  topCareType: CareType | null;
}

// ─── Pet Care: Earnings (private money dashboard) ─────────────────────────────

/**
 * The space's pet-care earnings dashboard — MEMBERS ONLY, like the ranking.
 * Every figure is DERIVED from bookings' `dailyRate`/`extraCharge` at read time
 * (`bookingTotal`); no money is ever stored pre-aggregated. Amounts are AUD,
 * rounded to cents.
 */
export interface PetEarnings {
  /** Earnings across every booking, all time. */
  totalEarned: number;
  /** Earnings from bookings starting in the current calendar year. */
  thisYear: number;
  /** Earnings from bookings starting in the current calendar month. */
  thisMonth: number;
  /** Mean take per PRICED booking (unpriced ones would drag it to nonsense). */
  avgPerBooking: number;
  /** Total earned ÷ care-days on priced bookings — the "what's a day worth" number. */
  avgPerCareDay: number;
  /**
   * How much came from daily rates vs one-off extras (they sum to totalEarned).
   * `fromExtraCharge` can be NEGATIVE when adjustments outweigh surcharges.
   */
  fromDailyRate: number;
  fromExtraCharge: number;
  /** Bookings that carry a price at all, and those that don't. */
  pricedBookings: number;
  unpricedBookings: number;
  /**
   * The unpriced bookings themselves, newest-first, so the dashboard can list
   * WHICH ones need a fee instead of only counting them. Capped at
   * `UNPRICED_SAMPLE_LIMIT` to keep the payload lean — `unpricedBookings` stays
   * the true total, so the UI can say "showing N of M" rather than truncating
   * silently.
   */
  unpricedSample: PetUnpricedBooking[];
  /** Earnings per month for the last 12 months (oldest → newest), zero-filled. */
  perMonth: { month: string; amount: number }[];
  /** Earnings per calendar year (oldest → newest). Only years with data appear. */
  perYear: { year: string; amount: number }[];
  /** Which kinds of care actually make the money, sorted desc. */
  byCareType: { key: CareType; amount: number }[];
  /** Top earning dogs, sorted desc. Multi-dog bookings split their total evenly. */
  dogs: PetDogEarning[];
}

/** How many unpriced bookings the earnings payload carries as examples. */
export const UNPRICED_SAMPLE_LIMIT = 100;

/**
 * A booking with no fee set, listed so it can be found and priced. Just enough
 * to identify it on the calendar — no notes, no ids we don't use.
 */
export interface PetUnpricedBooking {
  date: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD (inclusive); equals `date` for a single day
  petName: string;
  careType: CareType;
  assignedTo: string;
}

/** One dog's earnings row for the leaderboard. */
export interface PetDogEarning {
  name: string;
  /** This dog's share of earnings (an evenly-split slice of any multi-dog booking). */
  amount: number;
  /** Bookings that included this dog and carried a price. */
  bookings: number;
  /** Most-recent history photo for this dog, or null if none. */
  photoUrl: string | null;
  breed: string | null;
  /** See `PetDogStat.sex`. */
  sex: PetSex | null;
}

// ─── Pet Care: Ranking (private dog leaderboard) ──────────────────────────────

/**
 * The dog ranking — MEMBERS ONLY (the one private pets payload).
 *
 * **One order PER PERSON**, because Allen and Norah don't agree and a single
 * shared list forced a compromise that represented neither of them. Both
 * members can view and edit both orders: a space is shared, and seeing where
 * you disagree is the point.
 *
 * Only the ORDER is stored; the roster (avatar/name/breed/stats) is derived
 * client-side from the analysis dogs that have a History photo. Each order
 * holds normalized dog keys (trimmed + lowercased, the analysis `norm()` rule),
 * best-ranked first. Dogs in no order are simply unranked.
 */
export interface PetRanking {
  /** One ranked key list per person; always present, empty when never ranked. */
  orders: Record<Reader, string[]>;
  /** Absent until the ranking has been saved at least once. */
  updatedAt?: string;
  updatedByName?: string;
}

export interface UpdatePetRankingRequest {
  /** Whose ranking this write targets — validated against `READERS`. */
  person: Reader;
  order: string[];
}

// ─── Reading Notes (阅读笔记) ────────────────────────────────────────────────────

/** A bit of text that can be written in both languages; either side may be empty. */
export interface Bilingual {
  en: string;
  cn: string;
}

/** Who logged the book. */
export const READERS = ["Allen", "Norah"] as const;
export type Reader = (typeof READERS)[number];

export function isReader(v: unknown): v is Reader {
  return typeof v === "string" && (READERS as readonly string[]).includes(v);
}

/**
 * An empty per-person ranking map — one key per reader, so both the API and the
 * client build the same shape and adding a third person is one edit in one
 * place (`READERS`). Returns a FRESH object each call: a shared constant would
 * hand every caller the same mutable arrays.
 */
export function emptyRankingOrders(): Record<Reader, string[]> {
  return Object.fromEntries(READERS.map((r) => [r, [] as string[]])) as Record<Reader, string[]>;
}

// ─── Ratings (shared by books + movies) ───────────────────────────────────────
//
// Both shelves rate on 1–10 with HALF-star resolution (0.5 steps): a 1–5 scale
// made everything tie near the top once the list grew. Travel already rates
// cities this way, so the pattern (and its StarRating component) is proven.

/** Top of the rating scale. */
export const MAX_RATING = 10;
/** Smallest rating that still counts as rated. */
export const MIN_RATING = 0.5;

/** Is `v` a legal rating — a 0.5 step within 0.5..10? */
export function isValidRating(v: unknown): v is number {
  const n = Number(v);
  return Number.isFinite(n) && n >= MIN_RATING && n <= MAX_RATING && Math.round(n * 2) === n * 2;
}

/**
 * Read a stored rating on the 1–10 scale.
 *
 * Rows written before the scale change hold a 1–5 value. Rather than GUESS from
 * the number itself — which is ambiguous (is a stored `4` an old 4/5 or a new
 * 4/10?) and worse, not idempotent, so re-reading would double it again — every
 * row written from now on carries `ratingScale: 10`. Its absence is the marker
 * that a row predates the change, and only those are scaled up (4 → 8).
 *
 * Nothing is rewritten in the database: the raw value stays, and the
 * interpretation lives here, in one place.
 */
export function normalizeRating(stored: number, ratingScale?: number): number {
  if (!Number.isFinite(stored) || stored <= 0) return 0;
  const value = ratingScale === MAX_RATING ? stored : stored * 2;
  return Math.min(value, MAX_RATING);
}

/** A saved quote / good sentence. Bilingual; one per book may be the favorite. */
export interface BookQuote {
  text: Bilingual;
  favorite?: boolean;
}

/** Generous built-in genre/tag set (dropdown). Free enough to cover most books. */
export const BOOK_TAGS = [
  "Fiction",
  "Non-fiction",
  "Sci-Fi",
  "Fantasy",
  "Mystery",
  "Thriller",
  "Romance",
  "History",
  "Biography",
  "Memoir",
  "Philosophy",
  "Psychology",
  "Self-help",
  "Business",
  "Economics",
  "Science",
  "Technology",
  "Health",
  "Travel",
  "Cooking",
  "Art",
  "Poetry",
  "Children",
  "Classic",
  "Essay",
  "Religion",
  "Politics",
  "Education",
] as const;

export type BookTag = (typeof BOOK_TAGS)[number];

export interface ReadingBook {
  id: string;
  /** Cover image (Cloudinary). */
  coverUrl?: string;
  coverPublicId?: string;
  title: Bilingual;
  author: Bilingual;
  /** When the reader finished it (YYYY-MM-DD) — drives newest-first ordering. */
  finishedDate: string;
  /** 1–10 in 0.5 steps. Read through `normalizeRating` — see that helper. */
  rating: number;
  reader: Reader;
  tags: BookTag[];
  /** Reading feelings / 心得 (bilingual). */
  feelings: Bilingual;
  /** Good sentences / 好句摘录 (bilingual, with an optional favorite flag). */
  quotes: BookQuote[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateReadingBookRequest {
  coverUrl?: string;
  coverPublicId?: string;
  title: Bilingual;
  author: Bilingual;
  finishedDate: string; // YYYY-MM-DD
  rating: number; // 1–10, 0.5 steps
  /** Stamped by the server so `normalizeRating` can spot pre-1–10 rows. */
  ratingScale?: number;
  reader: Reader;
  tags: BookTag[];
  feelings: Bilingual;
  quotes: BookQuote[];
}

/**
 * Edit a book. Cover fields optional — omit to keep the current cover, or send a
 * new `coverUrl` + `coverPublicId` to replace it (backend deletes the old image).
 */
export type UpdateReadingBookRequest = Partial<CreateReadingBookRequest>;

// ─── Movies (观影记录) ────────────────────────────────────────────────────────
//
// The second shelf of the Reading section. Deliberately its OWN collection, not
// a `ReadingBook` with a "Movie" tag: a film has a director, a release year, a
// runtime and a venue (where you watched it), none of which mean anything on a
// book — and a book's author + `quotes[]` don't map onto a film. Merging them
// would make every field optional and every chart filter by kind. Same call we
// made for drinks-vs-dishes.
//
// What IS shared: `Bilingual`, `READERS`/`Reader` (the same two people), and the
// whole `reading.module.css` look, so the two shelves feel like one room.

/**
 * What kind of thing this is. A "movie" log in practice holds series too, and
 * 集数 only makes sense once you know which — so the kind drives whether the
 * form asks for seasons/episodes at all.
 */
export const MEDIA_KINDS = ["Movie", "TV", "Anime", "Other"] as const;

export type MediaKind = (typeof MEDIA_KINDS)[number];

export function isMediaKind(v: unknown): v is MediaKind {
  return typeof v === "string" && (MEDIA_KINDS as readonly string[]).includes(v);
}

/** A movie is always one sitting; everything else can span seasons/episodes. */
export function hasEpisodes(kind: MediaKind): boolean {
  return kind !== "Movie";
}

/**
 * How far through a series we are. Absent entirely for a film (always 1 of 1).
 * `ongoing` (连载中) means new episodes are still coming — so "12 episodes" is
 * a running count, not a finished total.
 */
export interface EpisodeProgress {
  /** 第 N 季. Omitted for something with no season structure. */
  seasons?: number;
  /** Episodes watched / released so far. */
  episodes?: number;
  /** 连载中 — still airing, so the counts above aren't final. */
  ongoing?: boolean;
}

export const MAX_SEASONS = 100;
export const MAX_EPISODES = 10000;

/** Film genres, ALPHABETICAL (drives the picker, the filter and the stats bars). */
export const MOVIE_GENRES = [
  "Action",
  "Adventure",
  "Animation",
  "Biography",
  "Comedy",
  "Crime",
  "Documentary",
  "Drama",
  "Family",
  "Fantasy",
  "History",
  "Horror",
  "Music",
  "Musical",
  "Mystery",
  "Romance",
  "Sci-Fi",
  "Sport",
  "Thriller",
  "War",
  "Western",
] as const;

export type MovieGenre = (typeof MOVIE_GENRES)[number];

/** Where it was watched — the stat that most distinguishes a film log. */
export const WATCH_VENUES = ["Cinema", "Streaming", "TV", "Flight", "Other"] as const;

export type WatchVenue = (typeof WATCH_VENUES)[number];

export function isWatchVenue(v: unknown): v is WatchVenue {
  return typeof v === "string" && (WATCH_VENUES as readonly string[]).includes(v);
}

/** Earliest plausible release year (Roundhay Garden Scene, 1888). */
export const MIN_RELEASE_YEAR = 1888;
/** Longest runtime we'll accept, in minutes — generous enough for any epic. */
export const MAX_RUNTIME_MINUTES = 600;

export interface Movie {
  id: string;
  /** Poster image (Cloudinary), 2:3 — the shape that says "film" at a glance. */
  posterUrl?: string;
  posterPublicId?: string;
  title: Bilingual;
  director: Bilingual;
  /** Movie · TV · Anime · Other — drives whether episode info applies. */
  kind: MediaKind;
  /**
   * Season/episode progress. Absent for a `kind: "Movie"` (always one sitting)
   * and for a series whose count we never bothered to record.
   */
  progress?: EpisodeProgress;
  /**
   * Release year and runtime are OPTIONAL on purpose: you log a film right after
   * watching it, and forcing a metadata lookup would turn logging into data
   * entry. Stats that need them skip the ones that lack them, and say so.
   */
  releaseYear?: number;
  /** Minutes — per episode for a series. */
  runtime?: number;
  /**
   * When we watched it (YYYY-MM-DD). EMPTY STRING means "sometime before we
   * started logging, exact date forgotten" — see `undated` below. Never invent
   * a date to fill this: a made-up date would pollute the pace chart.
   */
  watchedDate: string;
  /**
   * True when the date is genuinely unknown (watched before we started keeping
   * this list). Such rows still count in totals, ratings, genres and venue, but
   * are excluded from the 12-month pace chart and the this-year/this-month
   * counts, where a guessed date would be a lie.
   */
  undated?: boolean;
  /** 1–10 in 0.5 steps. Read through `normalizeRating` — see that helper. */
  rating: number;
  /**
   * Who watched it. Plural: most films here were watched together, and forcing
   * one name would either lose that or duplicate the row.
   */
  viewers: Reader[];
  /** ⭐ Marked as a favourite — the shortlist, independent of the rating. */
  favorite?: boolean;
  venue: WatchVenue;
  genres: MovieGenre[];
  /** 影评 / what we thought (bilingual). */
  review: Bilingual;
  /**
   * ONE memorable line, not a `quotes[]` list. A book accumulates many marked
   * passages as you read; a film leaves you with the single line you repeat.
   */
  favoriteLine: Bilingual;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMovieRequest {
  posterUrl?: string;
  posterPublicId?: string;
  title: Bilingual;
  director: Bilingual;
  kind: MediaKind;
  progress?: EpisodeProgress;
  releaseYear?: number;
  runtime?: number;
  /** YYYY-MM-DD, or "" together with `undated: true`. */
  watchedDate: string;
  undated?: boolean;
  rating: number; // 1–10, 0.5 steps
  /** Stamped by the server so `normalizeRating` can spot pre-1–10 rows. */
  ratingScale?: number;
  viewers: Reader[];
  favorite?: boolean;
  venue: WatchVenue;
  genres: MovieGenre[];
  review: Bilingual;
  favoriteLine: Bilingual;
}

/**
 * Every viewer of a movie, tolerating rows written before multi-viewer existed
 * (which carried a single `viewer`). One place so no caller has to remember.
 */
export function movieViewers(m: {
  viewers?: Reader[];
  viewer?: Reader;
}): Reader[] {
  if (m.viewers?.length) return m.viewers;
  return m.viewer ? [m.viewer] : [];
}

/** Was this watched by everyone (i.e. together)? */
export function watchedTogether(m: { viewers?: Reader[]; viewer?: Reader }): boolean {
  return movieViewers(m).length >= READERS.length;
}

/**
 * Edit a movie. Poster fields optional — omit to keep the current poster, or
 * send a new `posterUrl` + `posterPublicId` to replace it (backend deletes the
 * old image). Optional numbers cleared in the form are `$unset` server-side, so
 * a runtime can actually be removed rather than lingering.
 */
export type UpdateMovieRequest = Partial<CreateMovieRequest>;

// ─── Love Diary (恋爱日记) ────────────────────────────────────────────────────
//
// A couple's memory book: an Overview hero ("在一起 X 天" + two photos), important
// dates (countdown / count-up), and three "moment" grids (travel / the-moments /
// remarkable) of compact circular thumbnails. Reuses `Bilingual { en, cn }`.
// Lean: one settings doc (`coupleProfile`) + two collections (`diaryDates`,
// `diaryMoments`). Read-for-all, edit-for-admin.

/** One half of the couple, shown in the Overview hero. */
export interface CouplePartner {
  name: string; // display name
  photoUrl?: string; // round hero photo (via uploadImage())
  photoPublicId?: string; // Cloudinary public_id — for cleanup on replace
}

/** The single couple-profile settings doc that drives the Overview hero. */
export interface CoupleProfile {
  /** YYYY-MM-DD — "together since"; drives the "在一起 X 天" counter. */
  startDate: string;
  /** YYYY-MM-DD — wedding day; drives a second "结婚 X 天" counter. Optional. */
  marriedDate?: string;
  partners: [CouplePartner, CouplePartner];
  updatedAt: string;
}

export interface UpdateCoupleProfileRequest {
  startDate: string;
  /** Omit or empty to clear — not every couple profile has a wedding day. */
  marriedDate?: string;
  partners: [CouplePartner, CouplePartner];
}

/** How an important date recurs. `yearly` → birthdays/anniversaries. */
export const DIARY_REPEAT = ["none", "yearly"] as const;
export type DiaryRepeat = (typeof DIARY_REPEAT)[number];

export function isDiaryRepeat(v: unknown): v is DiaryRepeat {
  return typeof v === "string" && (DIARY_REPEAT as readonly string[]).includes(v);
}

/**
 * An important date. The UI computes 还有/已经 N 天 and the next yearly
 * occurrence from `date` + `repeat` — nothing time-relative is stored.
 */
export interface DiaryDate {
  id: string;
  title: Bilingual; // e.g. { en: "Wedding anniversary", cn: "结婚纪念日" }
  date: string; // YYYY-MM-DD anchor date
  repeat: DiaryRepeat; // "yearly" → repeats; "none" → one-off
  accent?: string; // optional colour stripe (defaults from a palette by index)
  createdAt: string;
  updatedAt: string;
}

export interface CreateDiaryDateRequest {
  title: Bilingual;
  date: string; // YYYY-MM-DD
  repeat: DiaryRepeat;
  accent?: string;
}

export type UpdateDiaryDateRequest = Partial<CreateDiaryDateRequest>;

/** The three moment-grid tabs share one shape, distinguished by `category`. */
export const DIARY_CATEGORIES = ["travel", "firstTime", "remarkable"] as const;
export type DiaryCategory = (typeof DIARY_CATEGORIES)[number];
// Labels: travel → 旅行时光 / Travel · firstTime → 美好瞬间 / The Moments
//         (key kept for stored data) · remarkable → 难忘时刻 / Remarkable

export function isDiaryCategory(v: unknown): v is DiaryCategory {
  return typeof v === "string" && (DIARY_CATEGORIES as readonly string[]).includes(v);
}

/** A moment in one of the three grids — a compact circular thumbnail. */
export interface DiaryMoment {
  id: string;
  category: DiaryCategory;
  title: Bilingual; // short label under the thumbnail
  date: string; // YYYY-MM-DD — shown under the thumbnail
  photoUrl?: string; // round thumbnail (optional)
  photoPublicId?: string;
  note?: Bilingual; // optional longer memory (Markdown) shown in the detail sheet
  createdAt: string;
  updatedAt: string;
}

export interface CreateDiaryMomentRequest {
  category: DiaryCategory;
  title: Bilingual;
  date: string; // YYYY-MM-DD
  photoUrl?: string;
  photoPublicId?: string;
  note?: Bilingual;
}

/**
 * Edit a moment. Photo fields optional — omit to keep the current photo, or send
 * a new `photoUrl` + `photoPublicId` to replace it (backend deletes the old one).
 */
export type UpdateDiaryMomentRequest = Partial<CreateDiaryMomentRequest>;

// ─── Pagination ───────────────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}
